import { useCallback, useMemo, useState } from "react";

import { Banner, Segmented } from "../../../shared/ui";
import { MasterPane } from "../AdminListRow";
import { ACCESS_VIEWS, adminHref, type AccessView } from "../adminRoute";
import { PermissionDenied } from "../PermissionDenied";
import {
  MANAGE_PERMISSION,
  type AccessApi,
  type MutationResult,
} from "./accessApi";
import type { AccessEnv } from "./accessEnv";
import { LoadGate } from "./LoadGate";
import { OperatorList } from "./OperatorList";
import { OperatorPage } from "./OperatorPage";
import { RoleList, RolePage } from "./RoleViews";
import { useAccessLoad } from "./useAccessLoad";
import { useAccessT } from "./useAccessT";

function isView(id: string): id is AccessView {
  return ACCESS_VIEWS.some((view) => view === id);
}

/**
 * Operators and roles. Wide: list beside the routed record. Narrow: the list
 * at /admin/access, the record on its own route. Mutations are offered only
 * with operators.manage; a server 403 degrades the section to read-only.
 */
export function AccessPage({
  api,
  wide,
  view,
  operatorId,
  roleId,
  onNavigate,
  onSignIn,
}: {
  api: AccessApi;
  wide: boolean;
  view: AccessView;
  operatorId: string | null;
  roleId: string | null;
  onNavigate: (href: string) => void;
  onSignIn: () => void;
}) {
  const t = useAccessT();
  const [token, setToken] = useState(0);
  const [refused, setRefused] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const loadMe = useCallback(() => api.me(), [api]);
  const loadRoles = useCallback(() => api.roles(), [api]);
  const me = useAccessLoad(loadMe, 0);
  const roles = useAccessLoad(loadRoles, token);

  const holdsManage =
    me.state.kind === "ready" &&
    me.state.data.permissions.includes(MANAGE_PERMISSION);
  const meKnown = me.state.kind === "ready";
  const roleItems = useMemo(
    () => (roles.state.kind === "ready" ? roles.state.data : []),
    [roles.state],
  );

  const env: AccessEnv = useMemo(
    () => ({
      api,
      canManage: holdsManage && !refused,
      roles: roleItems,
      onChanged: (result: MutationResult) => {
        setNotice(t(`admin.access.status.${result.status}`));
        setToken((current) => current + 1);
      },
      onDenied: () => {
        setRefused(true);
      },
      onSignIn,
    }),
    [api, holdsManage, refused, roleItems, onSignIn, t],
  );

  if (roles.state.kind === "denied") {
    return <PermissionDenied message={t("admin.denied.access")} />;
  }

  const go = (next: AccessView, id?: string) => {
    onNavigate(
      adminHref(
        next === "roles"
          ? { section: "access", accessView: next, roleId: id }
          : { section: "access", accessView: next, operatorId: id },
      ),
    );
  };
  const toList = () => {
    go(view);
  };

  const master = (
    <MasterPane label={t("admin.access.title")}>
      {view === "roles" ? (
        <LoadGate state={roles.state} retry={roles.retry}>
          {(items) => (
            <RoleList
              roles={items}
              selectedId={roleId}
              hrefFor={(id) =>
                adminHref({
                  section: "access",
                  accessView: "roles",
                  roleId: id,
                })
              }
              onOpen={(id) => {
                go("roles", id);
              }}
            />
          )}
        </LoadGate>
      ) : (
        <OperatorList
          env={env}
          token={token}
          selectedId={operatorId}
          hrefFor={(id) =>
            adminHref({
              section: "access",
              accessView: "operators",
              operatorId: id,
            })
          }
          onOpen={(id) => {
            go("operators", id);
          }}
        />
      )}
    </MasterPane>
  );

  let detail = null;
  if (view === "operators" && operatorId !== null) {
    detail = (
      <OperatorPage
        key={operatorId}
        env={env}
        operatorId={operatorId}
        token={token}
        onBack={wide ? undefined : toList}
        onNavigate={onNavigate}
      />
    );
  } else if (view === "roles" && roleId !== null) {
    detail = (
      <RolePage
        role={roleItems.find((role) => role.role_id === roleId)}
        onBack={wide ? undefined : toList}
      />
    );
  }

  const header = (
    <>
      {wide && (
        <h2 className="text-title text-fg">{t("admin.access.title")}</h2>
      )}
      {meKnown && !holdsManage && (
        <Banner tone="info">{t("admin.access.readOnly")}</Banner>
      )}
      {refused && holdsManage && (
        <Banner tone="warning">{t("admin.access.readOnlyDenied")}</Banner>
      )}
      {notice !== null && <Banner tone="success">{notice}</Banner>}
    </>
  );
  const switcher = (
    <Segmented
      label={t("admin.access.views")}
      value={view}
      options={ACCESS_VIEWS.map((id) => ({
        id,
        label: t(`admin.access.tab.${id}`),
      }))}
      onChange={(id) => {
        if (isView(id)) go(id);
      }}
    />
  );

  if (!wide && detail !== null) {
    return (
      <section className="flex flex-col gap-3">
        {header}
        {detail}
      </section>
    );
  }
  return (
    <section className="flex flex-col gap-3">
      {header}
      {switcher}
      {wide ? (
        <div className="grid min-w-0 grid-cols-[minmax(260px,340px)_minmax(0,1fr)] items-start gap-5">
          {master}
          {detail ?? (
            <p className="rounded-xl bg-sunken p-6 text-body text-fg-muted">
              {view === "roles"
                ? t("admin.access.pickRole")
                : t("admin.access.pick")}
            </p>
          )}
        </div>
      ) : (
        master
      )}
    </section>
  );
}
